import React, { useState, useCallback, useEffect } from 'react';
import SuccessBanner from './SuccessBanner';
import { propOr, pathOr, isEmpty, equals, compose, find, prop, values, map } from 'ramda';
import { Mutation } from 'react-apollo';
import moment from 'moment';
import { useQuery, useMutation } from '@apollo/react-hooks';
import {
  LockMinor,
  EditMajorMonotone,
  DeleteMajorMonotone,
  ViewMajorMonotone,
  CirclePlusMinor,
  SearchMajorMonotone,
} from '@shopify/polaris-icons';
import {
  CREATE_JOB,
  UPDATE_JOB,
  ADD_LOCATION,
  UPDATE_LOCATION,
  REMOVE_SHOP_LOCATION,
  ADD_MEMBER,
  UPDATE_MEMBER,
  REMOVE_MEMBER,
  CREATE_DEPARTMENT,
  ARCHIVE_JOB,
  UNARCHIVE_JOB,
} from '../apollo/mutation';
import {
  GET_JOB_LOCATIONS,
  GET_JOB,
  GET_JOBS,
  GET_CURRENT_SUBSCRIPTION,
  GET_HIRING_TEAM,
  GET_QUESTIONNAIRES,
} from '../apollo/queries';
import shortid from 'shortid';
import {
  Form,
  Page,
  Frame,
  Card,
  Button,
  ButtonGroup,
  FormLayout,
  EmptyState,
  Popover,
  TextField,
  TextStyle,
  Select,
  OptionList,
  Subheading,
  RadioButton,
  Toast,
  Modal,
  TextContainer,
  Heading,
  Spinner,
  Tooltip,
  Icon,
  Checkbox,
  Layout,
  InlineError,
  ResourceList,
  ResourceItem,
  Link,
  Pagination,
  Stack,
} from '@shopify/polaris';
import { Editor } from '@tinymce/tinymce-react';
import Loader from 'react-loader-spinner';
import csc from 'country-state-city';

import { onSubmitFailed } from '../../utils/errors';
import JobTranslationManager from './JobTranslationManager';

export const categories = [
  { label: 'Accounting', value: 'accounting' },
  { label: 'Administrative & Office', value: 'administrative and office' },
  { label: 'Cleaning & Facilities', value: 'cleaning and facilities' },
  { label: 'Customer Service', value: 'customer service' },
  { label: 'Design', value: 'design' },
  { label: 'Education', value: 'education' },
  { label: 'Engineering', value: 'engineering' },
  { label: 'Healthcare', value: 'healthcare' },
  { label: 'Human Resources', value: 'human resources' },
  {
    label: 'Installation, Maintenance & Repair',
    value: 'installation, mantenance and repair',
  },
  { label: 'Legal', value: 'legal' },
  { label: 'Management', value: 'managment' },
  { label: 'Marketing', value: 'marketing' },
  { label: 'Merchandising', value: 'merchandising' },
  { label: 'Personal Care Services', value: 'personal care services' },
  { label: 'Hospitality', value: 'hospitality' },
  { label: 'Retail', value: 'retail' },
  { label: 'Sales', value: 'sales' },
  {
    label: 'Social Services & Nonprofit',
    value: 'social services and nonprofit',
  },
  { label: 'Fitness', value: 'fitness' },
  { label: 'Logistics', value: 'logistics' },
  { label: 'Technology', value: 'technology' },
  { label: 'Production', value: 'production' },
  { label: 'Manufacturing', value: 'manufacturing' },
  { label: 'Food Production', value: 'food production' },
  { label: 'eCommerce', value: 'eCommerce' },
  { label: 'Finance', value: 'finance' },
  { label: 'NoCode', value: 'noCode' },
  { label: 'Operations', value: 'operations' },
  { label: 'Public Relations', value: 'public relations' },
];

const countries = [
  { label: 'Select a country', value: '' },
  { label: 'Australia', value: 'Australia', countryId: '13' },
  { label: 'Canada', value: 'Canada', countryId: '38' },
  { label: 'United Kingdom', value: 'United Kingdom', countryId: '230' },
  { label: 'United States', value: 'United States', countryId: '231' },
];

const currencies = [
  { label: 'Select a currency', value: '' },
  { label: 'US Dollar (USD)', value: 'USD' },
  { label: 'Euro (EUR)', value: 'EUR' },
  { label: 'British Pound (GBP)', value: 'GBP' },
  { label: 'Canadian Dollar (CAD)', value: 'CAD' },
  { label: 'Australian Dollar (AUD)', value: 'AUD' },
  { label: 'South African Rand (ZAR)', value: 'ZAR' },
];

const compensationTypes = [
  { label: 'Select a compensation type', value: '' },
  { label: 'Hourly', value: 'HOUR' },
  { label: 'Daily', value: 'DAY' },
  { label: 'Monthly', value: 'MONTH' },
  { label: 'Annually', value: 'YEAR' },
];

let companyStates = [];

const employments = ['Full-Time', 'Part-Time', 'Contractor', 'Temporary', 'Internship', 'Other'];

const initialState = {
  status: '',
  template: '',
  title: '',
  managerEmail: '',
  postalCode: '',
  city: '',
  country: '',
  state: '',
  description: '',
  employmentType: 'Full-Time',
  category: '',
  department: '',
  streetAddress: '',
  optionalAddress: '',
  isRemote: true,
  jobLocationName: 'remote',
  compensationEnabled: false,
  compensationType: '',
  currency: '',
  minCompensationRange: '0',
  maxCompensationRange: '0',
  plusComission: false,
  sendDisqualificationEmails: true,
  customQuestion: '',
  termsAndConditions: {
    disclaimerText: '',
    checkboxLabelText: '',
  },
};

const locationInitialState = {
  address1: '',
  address2: '',
  city: '',
  statee: '',
  country: '',
  zipCode: '',
  locationId: '',
  locationName: '',
};

const memberInitialState = {
  memberLastName: '',
  memberEmail: '',
  memberId: '',
  memberFirstName: '',
};

const memberErrorsInitialState = {
  memberFirstName: false,
  memberLastName: false,
  memberEmail: false,
  validEmail: false,
};

const locationErrorsInitialState = {
  locationName: false,
  city: false,
  statee: false,
  country: false,
  zipCode: false,
};

const JobSetup = (props) => {
  const node = document.getElementsByClassName('shop_container');
  const dataProps = JSON.parse(node[0].getAttribute('data-react-props'));
  const [isLoading, setLoading] = useState(false);
  const [active, setActive] = useState(false);
  const [titleUpdated, setTitleUpdated] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [checked, setChecked] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState(false);
  const [activeCollapse, setActiveCollapse] = useState(true);
  const [companyLocations, setCompanyLocations] = useState([]);
  const [customQuestions, setCustomQuestions] = useState([]);
  const [locationsToShow, setLocationsToShow] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedQuestionnaires, setSelectedQuestionnaires] = useState([]);
  const [lastQuestionId, setLastQuestionId] = useState(0);
  const [locationsPage, setLocationsPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = useCallback(() => setPopoverActive((popoverActive) => !popoverActive), []);
  const [formErrors, setFormErrors] = useState({
    title: false,
    category: false,
    department: false,
    employmentType: false,
    city: false,
    postalCode: false,
    country: false,
    state: false,
    description: false,
    jobLocationName: false,
    teamMemberIds: false,
    questionnaireIds: false,
    applicationFormFields: {
      phone: 'required',
      work_experience: 'disabled',
      linkedin_url: 'optional',
      github_url: 'disabled',
      portfolio_url: 'disabled',
      personal_website: 'disabled',
      resume: 'required',
      cover_letter: 'optional',
      newsletter_sign_up: 'optional',
    },
  });
  const [memberErrors, setMemberErrors] = useState(memberErrorsInitialState);
  const [locationErrors, setLocationErrors] = useState(locationErrorsInitialState);
  const [updateJob] = useMutation(UPDATE_JOB);
  const [updateLocation] = useMutation(UPDATE_LOCATION);
  const [updateMember] = useMutation(UPDATE_MEMBER);
  const [addLocation] = useMutation(ADD_LOCATION);
  const [addMember] = useMutation(ADD_MEMBER);
  const [deleteShopLocation] = useMutation(REMOVE_SHOP_LOCATION);
  const [deleteMember] = useMutation(REMOVE_MEMBER);
  const [createDepartment] = useMutation(CREATE_DEPARTMENT);
  const [archiveJob] = useMutation(ARCHIVE_JOB);
  const [unarchiveJob] = useMutation(UNARCHIVE_JOB);
  const [archiveModalActive, setArchiveModalActive] = useState(false);
  const min = 0;
  const max = 1000000;

  const handleToggle = useCallback(() => setActiveCollapse((activeCollapse) => !activeCollapse), []);

  const handleChange = useCallback((newChecked) => setChecked(newChecked), []);
  const handleModal = useCallback(() => setActive(!active), [active]);
  const toggleDuplicateModal = () => setDuplicateModal(!duplicateModal);

  let { history, shopifyDomain } = props;
  const [errors, setErrors] = useState('');
  const [form, setState] = useState(initialState);


  const [locationState, setLocationState] = useState(locationInitialState);
  const [memberState, setMemberState] = useState(memberInitialState);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [showEditor, setShowEditor] = useState(true);
  const [toastStatus, setToastStatus] = useState({ status: false, message: '' });

  const [locationModal, setLocationModal] = useState(false);
  const [isRemoveLocationModalOpen, setIsRemoveLocationModalOpen] = useState(false);
  const [createLocationModal, setCreateLocationModal] = useState(false);
  const [isCustomQuestionModalOpen, setIsCustomQuestionModalOpen] = useState(false);
  const [isCreateDepartmentModalOpen, setIsCreateDepartmentModalOpen] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [departmentError, setDepartmentError] = useState('');
  const [localDepartments, setLocalDepartments] = useState([]);

  const [memberModal, setMemberModal] = useState(false);
  const [isRemoveMemberModalOpen, setIsRemoveMemberModalOpen] = useState(false);
  const [createMemberModal, setCreateMemberModal] = useState(false);
  const [invalidQuestion, setInvalidQuestion] = useState(false);
  useEffect(() => {
    if (props.location.state) {
      document.documentElement.scrollTop = 0;
      setShowSuccessBanner(true);
    }
  }, [props.location.state]);

  const handleLocationModal = useCallback(() => {
    setLocationErrors(locationErrorsInitialState);
    setLocationModal(!locationModal);
  }, [locationModal]);
  const handleCreateLocationModal = useCallback(() => {
    setLocationErrors(locationErrorsInitialState);
    setCreateLocationModal(!createLocationModal);
  }, [createLocationModal]);

  const toggleIsCustomQuestionModalOpen = () => setIsCustomQuestionModalOpen(!isCustomQuestionModalOpen);
  const toggleIsRemoveLocationModalOpen = () => setIsRemoveLocationModalOpen(!isRemoveLocationModalOpen);
  const toggleIsCreateDepartmentModalOpen = () => {
    setIsCreateDepartmentModalOpen(!isCreateDepartmentModalOpen);
    setNewDepartmentName('');
    setDepartmentError('');
  };

  const handleDepartmentNameChange = (value) => {
    setNewDepartmentName(value);
    // Clear error when user starts typing
    if (departmentError) {
      setDepartmentError('');
    }
  };

  const handleDepartmentNameBlur = () => {
    // Validate on blur for better UX
    if (newDepartmentName.trim()) {
      // Check for duplicate department names (case-insensitive)
      const isDuplicate = localDepartments.some(
        (dept) => dept.headingText.toLowerCase() === newDepartmentName.trim().toLowerCase()
      );

      if (isDuplicate) {
        setDepartmentError('A department with this name already exists');
      }
    }
  };

  const handleMemberModal = useCallback(() => {
    setMemberErrors(memberErrorsInitialState);
    setMemberModal(!memberModal);
  }, [memberModal]);
  const handleCreateMemberModal = useCallback(() => {
    setMemberErrors(memberErrorsInitialState);
    setCreateMemberModal(!createMemberModal);
  }, [createMemberModal]);
  const toggleIsRemoveMemberModalOpen = () => setIsRemoveMemberModalOpen(!isRemoveMemberModalOpen);

  const handleEditMemberModal = (member) => {
    if (!!member) {
      let { id: memberId, firstName: memberFirstName, lastName: memberLastName, email: memberEmail } = member;
      setMemberState({
        memberId,
        memberFirstName,
        memberLastName,
        memberEmail,
      });
      setMemberModal(true);
    } else {
      setMemberState(memberInitialState);
      setCreateMemberModal(true);
    }
  };

  const handleRemoveMemberModal = (member) => {
    if (!!member) {
      let { id: memberId, firstName: memberFirstName, lastName: memberLastName, email: memberEmail } = member;
      setMemberState({
        memberId,
        memberFirstName,
        memberLastName,
        memberEmail,
      });
      toggleIsRemoveMemberModalOpen();
    }
  };

  const handleEditLocationModal = (location) => {
    if (location) {
      let { address1, address2, city, state, country, zipCode, id, name } = location;
      setLocationState({
        address1: address1,
        address2: address2,
        city: city,
        statee: state,
        country: country,
        zipCode: zipCode,
        locationName: name,
        locationId: id,
      });
      setLocationModal(true);
    } else {
      setLocationState(locationInitialState);
      setCreateLocationModal(true);
    }
  };

  const { data: templatesData } = useQuery(GET_JOBS, {
    variables: { shopifyToken: dataProps.shop.shopify_token, templates: true },
  });
  const { loading, data, refetch } = useQuery(GET_JOB_LOCATIONS, {
    variables: { shopifyToken: dataProps.shop.shopify_token },
  });
  const { loading: loadingT, data: subscriptionData, refetch: refetchT } = useQuery(GET_CURRENT_SUBSCRIPTION, {
    variables: { shopifyToken: dataProps.shop.shopify_token },
  });
  const { loading: loadingTeam, data: dataTeam, refetch: refetchTeam } = useQuery(GET_HIRING_TEAM, {
    variables: { shopifyToken: dataProps.shop.shopify_token },
  });
  const { data: dataQuestionnaires } = useQuery(GET_QUESTIONNAIRES, {
    variables: { shopifyToken: dataProps.shop.shopify_token },
  });

  const shop = propOr({}, 'shop')(subscriptionData);
  const openJobs = propOr({}, 'openJobs')(shop);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const isSubscribedUser = !isEmpty(currentSubscription);
  const careerPortal = shop?.careerPortal || {};
  const departments = localDepartments.length > 0 ? localDepartments : (careerPortal?.departments || []);
  const { questionnaires: allQuestionnaires = [] } = { ...dataQuestionnaires };
  const questionnaires = allQuestionnaires.filter(({ status }) => status === 'published');
  const subscriptionPlan = currentSubscription.subscriptionPlan || {
    number_jobs: 3,
  };
  const showApplicationFormButtons =
    isSubscribedUser &&
    (currentSubscription.subscriptionType === 'Premium' ||
      currentSubscription.subscriptionType === 'Growth' ||
      currentSubscription.subscriptionType === 'Business');

  useEffect(() => {
    const jobsTemplates = pathOr([], ['jobs', 'jobs'])(templatesData);

    if (isEmpty(templateOptions) && !isEmpty(jobsTemplates)) {
      let categories = jobsTemplates.map(({ category }) => category);
      categories = categories.filter((a, b) => categories.indexOf(a) === b).sort();
      const sections = categories.map((section) => {
        return {
          title: section,
          options: jobsTemplates
            .filter(({ category, archived }) => category === section && !archived)
            .map(({ id, title }) => ({
              label: title,
              value: id,
            })),
        };
      });
      setTemplateOptions([...sections]);
    }
  }, [templatesData]);

  let dataR, landingR, refetchR;

  let jobRefetch = null;
  if (props.match.params.id) {
    const { data: jobData, loading: jobLoading, refetch: refetch } = useQuery(GET_JOB, {
      variables: {
        shopifyToken: dataProps.shop.shopify_token,
        jobId: props.match.params.id,
      },
    });
    dataR = jobData;
    landingR = jobLoading;
    refetchR = refetch;
    jobRefetch = refetch;
  }

  const job = propOr({}, 'job')(dataR);

  // Redirect to view mode if job is archived
  useEffect(() => {
    if (props.match.params.id && job.archived) {
      history.replace(`/job-candidates/${props.match.params.id}`);
    }
  }, [job.archived, props.match.params.id, history]);

  const jobLocations = propOr([], 'jobLocations')(data);
  const hiringTeam = propOr([], 'hiringTeam')(dataTeam);
  const fixedItems = [{ isRemote: true, id: 998, name: 'remote' }];

  const customQuestionDefault = {
    customQuestionType: '',
    customQuestion: '',
    customQuestionOptions: [
      { value: '', id: shortid.generate() },
      { value: '', id: shortid.generate() },
    ],
  };

  useEffect(() => {
    if (!!hiringTeam && !!memberState && !!memberState.memberEmail) {
      const newMember = hiringTeam.find(({ email }) => email === memberState.memberEmail);
      if (!!newMember) {
        setSelectedMembers([...selectedMembers, newMember.id]);
        setMemberState(memberInitialState);
      }
    }
  }, [hiringTeam]);

  useEffect(() => {
    if (props.match.params.id) {
      // Only set default form state if job data is not loaded yet
      if (!job.title) {
        setState({
          ...form,
          applicationFormFields: {
            phone: 'required',
            address: 'required',
            work_experience: 'disabled',
            linkedin_url: 'optional',
            github_url: 'disabled',
            portfolio_url: 'disabled',
            personal_website: 'disabled',
            resume: 'required',
            cover_letter: 'optional',
            newsletter_sign_up: 'optional',
          },
          ...customQuestionDefault,
        });
      }
      const initialLocations = [...jobLocations.filter(({ name }) => name !== 'Custom'), ...fixedItems];
      setCompanyLocations(initialLocations);
      setLocationsToShow(initialLocations.slice(0, 5));
    }
  }, [jobLocations, job.title]);

  if (props.match.params.id) {
    useEffect(() => {
      if (job && job.title) {
        const jobApplicationFormFields = job.applicationFormFields ? job.applicationFormFields : {};
        
        // Create a new state object with job data
        const newState = {
          ...form,
          ...job,
          sendDisqualificationEmails: job.jobSettings ? job.jobSettings.sendDisqualificationEmails : true,
          department: job.department?.id,
          jobLocationName: job.locationName,
          minCompensationRange: job.minCompensationRange ? job.minCompensationRange.toString() : '0',
          maxCompensationRange: job.maxCompensationRange ? job.maxCompensationRange.toString() : '0',
          applicationFormFields: {
            phone: 'required',
            address: 'required',
            work_experience: 'disabled',
            linkedin_url: 'optional',
            github_url: 'disabled',
            portfolio_url: 'disabled',
            personal_website: 'disabled',
            resume: 'required',
            cover_letter: 'optional',
            newsletter_sign_up: 'optional',
            ...jobApplicationFormFields,
          },
          ...customQuestionDefault,
        };
        
        setState(newState);
        const customQuestionsLength = job?.customQuestions?.length || 0;
        const customQuestionsFetched = customQuestionsLength
          ? job.customQuestions.map((question, index) => ({
              id: question.id || `${question.question[0]}-${index}`,
              ...question,
            }))
          : [];
        setCustomQuestions(customQuestionsFetched);
        setLastQuestionId(customQuestionsLength ? job.customQuestions.length : 0);
        setActiveCollapse(!job.isRemote);
        setSelectedMembers((job.teamMembers || []).map(({ id }) => id));
        setSelectedQuestionnaires((job.questionnaires || []).map(({ id }) => id));
        
        // Set template ID if job has a template
        if (job.template) {
          setSelectedTemplateId(job.template);
        }
      }
      if (refetchR) refetchR();
    }, [job]);
  } else {
    useEffect(() => {
      if (jobLocations.length > 0) {
        let defaultAddress = '';
        if (
          currentSubscription.subscriptionType === 'Premium' ||
          currentSubscription.subscriptionType === 'Growth' ||
          currentSubscription.subscriptionType === 'Business'
        ) {
          defaultAddress = 'disabled';
        } else {
          defaultAddress = 'required';
        }
        setState({
          ...form,
          applicationFormFields: {
            phone: 'required',
            address: defaultAddress,
            work_experience: 'disabled',
            linkedin_url: 'optional',
            github_url: 'disabled',
            portfolio_url: 'disabled',
            personal_website: 'disabled',
            resume: 'required',
            cover_letter: 'optional',
            newsletter_sign_up: 'optional',
          },
          ...customQuestionDefault,
        });
        const initialLocations = [...jobLocations.filter(({ name }) => name !== 'Custom'), ...fixedItems];
        setCompanyLocations(initialLocations);
        setLocationsToShow(initialLocations.slice(0, 5));
      }
    }, [jobLocations]);
  }

  // Auto-select the first hiring team member only when creating a new job.
  // Previously used `form.title == ''` as the guard, but that condition is also
  // true on every remount of the edit form before job data loads. Because React
  // runs effects in definition order within the same render cycle, this effect
  // fired AFTER useEffect([job]) set the correct members — but before setState
  // for form.title had committed — so it kept overwriting the selection back to
  // just the first member every time the edit page was opened a second time.
  // Using !props.match.params.id is the correct guard: it only triggers for new
  // job creation where no job ID exists in the route.
  useEffect(() => {
    if (hiringTeam && hiringTeam.length > 0 && !props.match.params.id) {
      setSelectedMembers([hiringTeam[0].id]);
    }
  }, [dataTeam]);

  // Initialize local departments when subscription data loads
  useEffect(() => {
    if (careerPortal?.departments && localDepartments.length === 0) {
      setLocalDepartments(careerPortal.departments);
    }
  }, [careerPortal?.departments, localDepartments.length]);

  // Ensure job's department is in local departments when editing
  useEffect(() => {
    if (job?.department && localDepartments.length > 0) {
      const jobDepartmentExists = localDepartments.some(dept => dept.id === job.department.id);
      if (!jobDepartmentExists) {
        setLocalDepartments([...localDepartments, job.department]);
      }
    }
  }, [job?.department, localDepartments.length]);

  const validateForm = () => {
    return !(
      isEmpty(form?.title) ||
      isEmpty(form?.category) ||
      isEmpty(form?.description) ||
      isEmpty(form?.employmentType) ||
      isEmpty(selectedMembers) ||
      isEmpty(form?.jobLocationName)
    );
  };

  const setJobState = ({ name, value, form }) => {
    if (name === 'state') return value;
    if (name === 'country' || (name === 'isRemote' && value)) return '';
    return form.state;
  };

  const updateField = (name, questionOptionId) => (value) => {
    if (name === 'isRemote') setActiveCollapse(!activeCollapse);
    setFormErrors({ ...formErrors, [name]: false });

    if (name === 'country') {
      const countryId = compose(
        prop('countryId'),
        find(({ label }) => label === value)
      )(countries);
      const countryState = compose(
        map(({ name }) => ({ label: name, value: name })),
        csc.getStatesOfCountry
      )(countryId);
      companyStates = [{ label: 'Select a state', value: '' }, ...countryState];
    }

    const isRemoteActive = name === 'isRemote' && value;

    const location = {
      streetAddress: form.streetAddress,
      city: form.city,
      postalCode: form.postalCode,
      country: form.country,
      state: form.state,
      optionalAddress: form.optionalAddress,
    };

    if (isRemoteActive) {
      location.streetAddress = '';
      location.city = '';
      location.postalCode = '';
      location.country = '';
      location.state = '';
      location.optionalAddress = '';
    }

    let updatedOptions = [...form.customQuestionOptions];

    if (name === 'customQuestionOptions') {
      updatedOptions = form.customQuestionOptions.map(({ id, ...option }) => {
        if (id === questionOptionId) {
          return { id, ...option, value };
        }
        return { id, ...option };
      });
      setState({
        ...form,
        ...location,
        customQuestionOptions: updatedOptions,
        state: setJobState({ name, value, form }),
      });
    } else if (name === 'disclaimerText' || name === 'checkboxLabelText') {
      setState({
        ...form,
        ...location,
        customQuestionOptions: updatedOptions,
        termsAndConditions: { ...form.termsAndConditions, [name]: value },
        state: setJobState({ name, value, form }),
      });
    } else {
      setState({
        ...form,
        ...location,
        customQuestionOptions: updatedOptions,
        [name]: value,
        state: setJobState({ name, value, form }),
      });
    }
  };

  const handleEditorChange = (content) => {
    setState({
      ...form,
      description: content,
    });
  };

  const modalMarkup = isLoading ? (
    <TextContainer>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <div style={{ textAlign: 'center' }}>
          <Loader type='TailSpin' color='#6371c7' height={40} width={40} />
          <p> Saving job data </p>
        </div>
      </div>
    </TextContainer>
  ) : (
    <TextContainer>
      <p> Job Title: {form.title} </p>
      <p>
        {' '}
        Category:{' '}
        {compose(
          propOr(form.category, 'label'),
          find((c) => c.value == form.category)
        )(categories)}{' '}
      </p>
      <p>
        {' '}
        Department:{' '}
        {compose(
          propOr(form.department, 'headingText'),
          find((c) => c.id == form.department)
        )(departments)}{' '}
      </p>
      <p> Location: {form.isRemote ? 'Remote' : `${form.city} ${form.state}, ${form.postalCode}`} </p>
    </TextContainer>
  );

  const handleFormSubmit = (props) => {
    let { createJob } = props;

    setActive(!active);
  };

  // Must be async so we can await the mutation before calling refetch().
  // Previously this was synchronous — updateJob() returned a Promise that was
  // never awaited, so refetch() fired immediately before the server had committed
  // the changes. The refetch returned stale data which then reset selectedMembers
  // back to the old hiring team via useEffect([job]).
  const updateJobForm = async () => {
    setLoading(true);
    const updatedErrors = {
      title: compose(isEmpty, prop('title'))(form),
      category: compose(isEmpty, prop('category'))(form),
      department: compose(isEmpty, propOr('department'))(form),
      employmentType: compose(isEmpty, prop('employmentType'))(form),
      city: compose(isEmpty, prop('city'))(form),
      postalCode: compose(isEmpty, prop('postalCode'))(form),
      country: compose(isEmpty, prop('country'))(form),
      state: compose(isEmpty, prop('state'))(form),
      description: compose(isEmpty, prop('description'))(form),
      jobLocationName: compose(isEmpty, prop('jobLocationName'))(form),
      teamMemberIds: isEmpty(selectedMembers),
    };

    if (form.isRemote) {
      updatedErrors.city = false;
      updatedErrors.postalCode = false;
      updatedErrors.country = false;
      updatedErrors.state = false;
    }

    setFormErrors(updatedErrors);

    if (
      compose(
        find((item) => item),
        values
      )(updatedErrors)
    ) {
      setTimeout(() => {
        onSubmitFailed();
      }, 10);
    } else {
      setLoading(true);
      const hiringIds = hiringTeam.map(({ id }) => id);
      const qsIds = questionnaires.map(({ id }) => id);
      const teamMemberIds = (selectedMembers || []).filter((memberId) => hiringIds.indexOf(memberId) !== -1);
      const questionnaireIds = (selectedQuestionnaires || []).filter((questionnaireId) => qsIds.indexOf(questionnaireId) !== -1);
      // await ensures refetch() is only called after the mutation has fully
      // resolved on the server, so the re-fetched job contains the updated data.
      const response = await updateJob({
        variables: {
          jobId: props.match.params.id,
          status: form.status,
          template: form.template,
          title: form.title,
          description: form.description,
          employmentType: form.employmentType,
          category: form.category,
          department: form.department,
          managerEmail: form.managerEmail,
          country: form.country,
          state: form.state,
          city: form.city,
          postalCode: form.postalCode,
          shopifyToken: dataProps.shop.shopify_token,
          isRemote: form.isRemote,
          streetAddress: form.streetAddress,
          optionalAddress: form.optionalAddress,
          compensationEnabled: form.compensationEnabled,
          compensationType: form.compensationType,
          currency: form.currency,
          minCompensationRange: form.minCompensationRange,
          maxCompensationRange: form.maxCompensationRange,
          plusComission: form.plusComission,
          applicationFormFields: form.applicationFormFields,
          customQuestions,
          teamMemberIds,
          questionnaireIds,
          locationName: form.jobLocationName,
          sendDisqualificationEmails: form.sendDisqualificationEmails,
        },
      });

      const errors = pathOr([], ['data', 'updateJob', 'errors'])(response);
      const emptyErrors = isEmpty(errors);
      if (emptyErrors) {
        refetch();
        refetchTeam();
        refetchT();
        setTitleUpdated(true);
        setLoading(true);
        document.documentElement.scrollTop = 0;
        setShowSuccessBanner(true);
        setToastStatus({ status: true, message: 'The job was updated successfully.' });
      } else {
        setToastStatus({ status: true, message: 'There is an error with the server' });
      }
    }
  };

  const onArchiveJob = async () => {
    setLoading(true);
    try {
      const response = await archiveJob({
        variables: {
          jobId: props.match.params.id,
          shopifyToken: dataProps.shop.shopify_token,
        },
      });

      const errors = pathOr([], ['data', 'archiveJob', 'errors'])(response);
      if (isEmpty(errors)) {
        setToastStatus({ status: true, message: 'Job archived successfully' });
        refetch();
      } else {
        setToastStatus({ status: true, message: 'There is an error with the server' });
      }
    } catch (error) {
      setToastStatus({ status: true, message: 'There is an error with the server' });
    } finally {
      setLoading(false);
      setArchiveModalActive(false);
    }
  };

  const onUnarchiveJob = async () => {
    setLoading(true);
    try {
      const response = await unarchiveJob({
        variables: {
          jobId: props.match.params.id,
          shopifyToken: dataProps.shop.shopify_token,
        },
      });

      const errors = pathOr([], ['data', 'unarchiveJob', 'errors'])(response);
      if (isEmpty(errors)) {
        setToastStatus({ status: true, message: 'Job unarchived successfully' });
        refetch();
      } else {
        setToastStatus({ status: true, message: 'There is an error with the server' });
      }
    } catch (error) {
      setToastStatus({ status: true, message: 'There is an error with the server' });
    } finally {
      setLoading(false);
      setArchiveModalActive(false);
    }
  };

  const createJobFromModal = (props) => {
    let { createJob, status, duplicate = false } = props;
    setDuplicateModal(false);
    setLoading(true);

    createJob({
      variables: {
        template: form.template,
        title: duplicate ? `Copy of ${form.title}` : form.title,
        description: form.description,
        department: form.department,
        employmentType: form.employmentType,
        category: form.category,
        managerEmail: form.managerEmail,
        country: form.country,
        state: form.state,
        city: form.city,
        postalCode: form.postalCode,
        shopifyToken: dataProps.shop.shopify_token,
        status: duplicate ? 'closed' : status,
        isRemote: form.isRemote,
        streetAddress: form.streetAddress,
        optionalAddress: form.optionalAddress,
        compensationEnabled: form.compensationEnabled,
        compensationType: form.compensationType,
        currency: form.currency,
        minCompensationRange: form.minCompensationRange,
        maxCompensationRange: form.maxCompensationRange,
        plusComission: form.plusComission,
        applicationFormFields: form.applicationFormFields,
        customQuestions,
        teamMemberIds: selectedMembers || [],
        questionnaireIds: selectedQuestionnaires || [],
        locationName: form.jobLocationName,
        sendDisqualificationEmails: form.sendDisqualificationEmails,
      },
    })
      .then((response) => {
        const location = {
          pathname: `/set-job/${response.data.createJob.job.id}`,
          state: true,
        };
        if (response.data.createJob.errors.length !== 0) {
          setActive(false);
          console.log(response.data.createJob.errors);
          setToastStatus({ status: true, message: 'There is an error with the server' });
        } else {
          setToastStatus({ status: true, message: `The job was ${duplicate ? 'duplicated' : 'created'} successfully.` });
          setActive(false);
          setLoading(false);
          history.push(location);
        }
      })
      .catch((e) => {
        const [error] = [...(e.graphQLErrors || [])];
        const { message } = { ...error };
        setLoading(false);
        setErrors(message);
        setToastStatus({ status: true, message: 'There is an error with the server' });
      });
  };

  const onChangeTemplateSelect = (item) => {
    const template = compose(
      find(({ id }) => equals(item, id)),
      pathOr([], ['jobs', 'jobs'])
    )(templatesData);

    setSelectedTemplateId(template.id);

    const newFormState = template
      ? {
          ...initialState,
          ...template,
          ...customQuestionDefault,
          isRemote: true,
          department: !!template.department ? template.department.id : '',
          jobLocationName: 'remote',
          applicationFormFields: form.applicationFormFields,
          minCompensationRange: template.minCompensationRange ? template.minCompensationRange.toString() : '0',
          maxCompensationRange: template.maxCompensationRange ? template.maxCompensationRange.toString() : '0',
        }
      : {
          ...form,
          ...job,
          minCompensationRange: job.minCompensationRange ? job.minCompensationRange.toString() : '0',
          maxCompensationRange: job.maxCompensationRange ? job.maxCompensationRange.toString() : '0',
        };

    setShowEditor(false);
    setTimeout(() => {
      setShowEditor(true);
    }, 300);
    setState(newFormState);
    setPopoverActive(false);
  };

  const updateLocationFromSelect = (item, name) => {
    const { address1 = '', city = '', country = '', name: jobLocationName = '', state = '', zipCode = '', isRemote = false } = {
      ...item,
    };
    if (name === 'custom') {
      return;
    }

    let location = {
      ...form,
      jobLocationName,
      country,
      city,
      state,
      streetAddress: address1,
      postalCode: zipCode,
      isRemote,
    };
    setFormErrors({ ...formErrors, [name]: false });
    setState(location);
  };

  const onRenderLocationItems = (item) => {
    const { address1, city, country, name, zipCode, isRemote = false, isAddButton = false, id, state } = { ...item };
    const checked = form.jobLocationName === name || (isRemote && form.isRemote);
    const onClick = isAddButton ? () => handleEditLocationModal() : undefined;
    return (
      <ResourceList.Item id={`${id}`} accessibilityLabel={`${name ? name : isRemote ? 'Remote' : 'Add'}`} onClick={onClick}>
        <tr style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <td style={{ display: 'flex', flexDirection: 'row', width: '60%' }}>
            <div style={{ paddingRight: '1rem' }}>
              {!isAddButton && (
                <RadioButton checked={checked} onChange={() => updateLocationFromSelect(item, 'jobLocationName')} />
              )}
              {isAddButton && <Icon source={CirclePlusMinor} />}
            </div>
            {!isRemote && !isAddButton && (
              <div onClick={() => updateLocationFromSelect(item)}>
                <TextStyle variation='strong'>{name}</TextStyle>
                <br />
                <TextStyle>
                  {address1}, {city}, {state}, {country}, {zipCode}
                </TextStyle>
              </div>
            )}
            {isRemote && !isAddButton && (
              <div onClick={() => updateLocationFromSelect(item)}>
                <TextStyle variation='strong'>Remote</TextStyle>
                <br />
                <TextStyle>Workers can telecommute for this job</TextStyle>
              </div>
            )}
            {isAddButton && <TextStyle variation='subdued'>Add location</TextStyle>}
          </td>
          <td style={{ width: '155px' }} className='location-buttons-container'>
            {!isRemote && !isAddButton && (
              <>
                <Button onClick={() => handleEditLocationModal(item)} size='small'>
                  Edit
                </Button>
                <Button
                  onClick={() => {
                    setLocationState({ locationId: id, address1, locationName: name });
                    toggleIsRemoveLocationModalOpen();
                  }}
                  size='small'>
                  Remove
                </Button>
              </>
            )}
          </td>
        </tr>
      </ResourceList.Item>
    );
  };

  const renderButton = props.match.params.id ? (
    <Button primary={true} onClick={() => updateJobForm()} size='large' disabled={!validateForm()}>
      Update
    </Button>
  ) : (
    <Button primary={true} submit={true} size='large'>
      Save
    </Button>
  );

  const onSubmit = (action) => {
    const updatedErrors = {
      title: compose(isEmpty, prop('title'))(form),
      category: compose(isEmpty, prop('category'))(form),
      department: compose(isEmpty, propOr('', 'department'))(form),
      employmentType: compose(isEmpty, prop('employmentType'))(form),
      city: compose(isEmpty, prop('city'))(form),
      postalCode: compose(isEmpty, prop('postalCode'))(form),
      country: compose(isEmpty, prop('country'))(form),
      state: compose(isEmpty, prop('state'))(form),
      description: compose(isEmpty, prop('description'))(form),
      jobLocationName: compose(isEmpty, prop('jobLocationName'))(form),
      teamMemberIds: isEmpty(selectedMembers),
    };

    if (form.isRemote) {
      updatedErrors.city = false;
      updatedErrors.postalCode = false;
      updatedErrors.country = false;
      updatedErrors.state = false;
    }

    setFormErrors(updatedErrors);
    if (
      compose(
        find((item) => item),
        values
      )(updatedErrors)
    ) {
      setTimeout(() => {
        onSubmitFailed();
      }, 10);
    } else {
      handleFormSubmit(action);
    }
  };

  const updateLocationField = (name) => (value) => {
    setLocationState({
      ...locationState,
      [name]: value,
    });
    setLocationErrors({
      ...locationErrors,
      [name]: false,
    });
  };

  const updateMemberField = (name) => (value) => {
    setMemberState({
      ...memberState,
      [name]: value,
    });
    setMemberErrors({
      ...memberErrors,
      [name]: false,
      validEmail: false,
    });
  };

  const modalLocationMarkup = (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit({ updateLocation });
      }}>
      <FormLayout>
        <div>
          <TextField
            id='locationName'
            label='Name'
            value={locationState.locationName}
            name='locationName'
            onChange={updateLocationField('locationName')}
          />
          {locationErrors.locationName && <InlineError message='This field is required' fieldID='locationName' />}
        </div>
        <TextField
          id='address1'
          label='Address'
          value={locationState.address1}
          name='address1'
          onChange={updateLocationField('address1')}
        />
        <TextField
          id='address2'
          label='Apartment, suite, etc (Optional)'
          value={locationState.address2}
          name='address2'
          onChange={updateLocationField('address2')}
        />
        <FormLayout.Group>
          <div>
            <TextField id='city' label='City' value={locationState.city} name='city' onChange={updateLocationField('city')} />
            {locationErrors.city && <InlineError message='This field is required' fieldID='city' />}
          </div>
          <div>
            <TextField
              id='zipCode'
              label='Zip code'
              value={locationState.zipCode}
              name='zipCode'
              onChange={updateLocationField('zipCode')}
            />
            {locationErrors.zipCode && <InlineError message='This field is required' fieldID='zipCode' />}
          </div>
        </FormLayout.Group>
        <FormLayout.Group>
          <div>
            <TextField
              id='country'
              label='Country'
              value={locationState.country}
              name='country'
              onChange={updateLocationField('country')}
            />
            {locationErrors.country && <InlineError message='This field is required' fieldID='country' />}
          </div>
          <div>
            <TextField
              id='state'
              label='State'
              value={locationState.statee}
              name='state'
              onChange={updateLocationField('statee')}
            />
            {locationErrors.statee && <InlineError message='This field is required' fieldID='statee' />}
          </div>
        </FormLayout.Group>
      </FormLayout>
    </Form>
  );

  const modalMemberMarkup = (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit({ updateMember });
      }}>
      <FormLayout>
        <FormLayout.Group>
          <div>
            <TextField
              id='memberFirstName'
              label='First name'
              value={memberState.memberFirstName}
              name='memberFirstName'
              onChange={updateMemberField('memberFirstName')}
            />
            {memberErrors.memberFirstName && <InlineError message='This field is required' fieldID='memberFirstName' />}
          </div>
          <div>
            <TextField
              id='memberLastName'
              label='Last name'
              value={memberState.memberLastName}
              name='memberLastName'
              onChange={updateMemberField('memberLastName')}
            />
            {memberErrors.memberLastName && <InlineError message='This field is required' fieldID='memberLastName' />}
          </div>
          <div>
            <TextField
              id='memberEmail'
              label='Email'
              value={memberState.memberEmail}
              name='memberEmail'
              onChange={updateMemberField('memberEmail')}
            />
            {memberErrors.memberEmail && <InlineError message='This field is required' fieldID='memberEmail' />}
            {memberErrors.validEmail && <InlineError message='Invalid email address' fieldID='memberEmail' />}
          </div>
        </FormLayout.Group>
      </FormLayout>
    </Form>
  );

  const updateLocationFromModal = (props) => {
    setLocationErrors(locationErrorsInitialState);
    updateLocation({
      variables: {
        name: locationState.locationName,
        address1: locationState.address1,
        address2: locationState.address2,
        city: locationState.city,
        state: locationState.statee,
        country: locationState.country,
        zipCode: locationState.zipCode,
        locationId: locationState.locationId,
        shopifyToken: dataProps.shop.shopify_token,
      },
    })
      .then((response) => {
        if (response.data.updateLocation.errors.length !== 0) {
          alert(response.data.updateLocation.errors);
          const { data } = response;
          console.log(data);
        } else {
          setLocationModal(!locationModal);
          const { data } = response;
          console.log(data);
          const { address1 = '', city = '', country = '', name: jobLocationName = '', state = '', zipCode = '', isRemote = false } = data.updateLocation.location;

          if (form.jobLocationName === jobLocationName) {
            let location = {
              ...form,
              jobLocationName,
              country,
              city,
              state,
              streetAddress: address1,
              postalCode: zipCode,
              isRemote,
            };
            setState(location);
          }
        }
        refetch();
      })
      .catch((e) => {
        let messages = e.graphQLErrors[0].message;
        setErrors(messages);
      });
  };

  const updateMemberFromModal = (props) => {
    if (!isValidEmail(memberState.memberEmail)) {
      setMemberErrors({
        ...memberErrors,
        validEmail: true,
      });
    } else {
      setLoading(true);
      setMemberErrors(memberErrorsInitialState);
      updateMember({
        variables: {
          firstName: memberState.memberFirstName,
          lastName: memberState.memberLastName,
          email: memberState.memberEmail,
          memberId: memberState.memberId,
          shopifyToken: dataProps.shop.shopify_token,
        },
      })
        .then((response) => {
          setLoading(false);
          if (response.data.updateTeamMember.errors.length !== 0) {
            alert(response.data.updateTeamMember.errors);
          } else {
            setMemberModal(!memberModal);
          }
          refetchTeam();
        })
        .catch((e) => {
          setLoading(false);
          let messages = e.graphQLErrors[0].message;
          setErrors(messages);
        });
    }
  };

  const addLocationFromModal = () => {
    setLocationErrors(locationErrorsInitialState);
    addLocation({
      variables: {
        address1: locationState.address1,
        address2: locationState.address2,
        city: locationState.city,
        country: locationState.country,
        state: locationState.statee,
        zipCode: locationState.zipCode,
        name: locationState.locationName,
        shopifyToken: dataProps.shop.shopify_token,
      },
    })
      .then((response) => {
        if (response.data.addLocation.errors.length !== 0) {
          alert(response.data.addLocation.errors);
          const { data } = response;
          console.log(data);
        } else {
          setCreateLocationModal(!createLocationModal);
          refetch();
          const { data } = response;
          console.log(data);
        }
      })
      .catch((e) => {
        console.log(e);
        let messages = e.graphQLErrors[0].message;
        setErrors(messages);
      });
  };

  const validateMemberFields = () => {
    const updatedMemberErrors = {
      memberFirstName: compose(isEmpty, prop('memberFirstName'))(memberState),
      memberLastName: compose(isEmpty, prop('memberLastName'))(memberState),
      memberEmail: compose(isEmpty, prop('memberEmail'))(memberState),
    };
    setMemberErrors(updatedMemberErrors);
  };

  const isValidEmail = (memberEmail) => /[\w\d\.-]+@[\w\d\.-]+\.[\w\d\.-]+/.test(memberEmail);

  const stateMemberFields = () => {
    return isEmpty(memberState?.memberFirstName) || isEmpty(memberState?.memberLastName) || isEmpty(memberState?.memberEmail);
  };

  const validateLocationFields = () => {
    const updatedLocationErrors = {
      locationName: compose(isEmpty, prop('locationName'))(locationState),
      city: compose(isEmpty, prop('city'))(locationState),
      statee: compose(isEmpty, prop('statee'))(locationState),
      country: compose(isEmpty, prop('country'))(locationState),
      zipCode: compose(isEmpty, prop('zipCode'))(locationState),
    };
    setLocationErrors(updatedLocationErrors);
  };

  const stateLocationFields = () => {
    return (
      isEmpty(locationState?.locationName) ||
      isEmpty(locationState?.city) ||
      isEmpty(locationState?.statee) ||
      isEmpty(locationState?.zipCode) ||
      isEmpty(locationState?.country)
    );
  };

  const addMemberFromModal = () => {
    if (!isValidEmail(memberState.memberEmail)) {
      setMemberErrors({
        ...memberErrors,
        validEmail: true,
      });
    } else {
      setLoading(true);
      setMemberErrors(memberErrorsInitialState);
      addMember({
        variables: {
          firstName: memberState.memberFirstName,
          lastName: memberState.memberLastName,
          email: memberState.memberEmail,
          shopifyToken: dataProps.shop.shopify_token,
        },
      })
        .then((response) => {
          setLoading(false);
          if (response.data.addTeamMember.errors.length !== 0) {
            alert(response.data.addTeamMember.errors);
          } else {
            setCreateMemberModal(!createMemberModal);
            refetchTeam();
          }
        })
        .catch((e) => {
          setLoading(false);
          let messages = e.graphQLErrors[0].message;
          setErrors(messages);
        });
    }
  };

  const handleCreateDepartment = () => {
    if (!newDepartmentName.trim()) {
      setDepartmentError('Department name is required');
      return;
    }

    // Check for duplicate department names (case-insensitive)
    const isDuplicate = localDepartments.some(
      (dept) => dept.headingText.toLowerCase() === newDepartmentName.trim().toLowerCase()
    );

    if (isDuplicate) {
      setDepartmentError('A department with this name already exists');
      return;
    }

    setLoading(true);
    createDepartment({
      variables: {
        headingText: newDepartmentName.trim(),
        shopifyToken: dataProps.shop.shopify_token,
      },
    })
      .then((response) => {
        setLoading(false);
        if (response.data.createDepartment.errors && response.data.createDepartment.errors.length > 0) {
          setDepartmentError(response.data.createDepartment.errors[0]);
        } else {
          const newDepartment = response.data.createDepartment.department;
          
          // Update local departments state with the new department
          const updatedLocalDepartments = [...localDepartments, newDepartment];
          setLocalDepartments(updatedLocalDepartments);

          // Auto-select the new department
          setState({
            ...form,
            department: newDepartment.id,
          });

          // Clear form errors for department since we now have a valid selection
          setFormErrors({
            ...formErrors,
            department: false,
          });

          toggleIsCreateDepartmentModalOpen();
          setToastStatus({ status: true, message: 'Department created successfully' });
        }
      })
      .catch((e) => {
        setLoading(false);
        const errorMessage = e.graphQLErrors?.[0]?.message || 'Failed to create department';
        setDepartmentError(errorMessage);
      });
  };

  const toggleAppFormFields = ({ fieldName, value }) => () => {
    setState({
      ...form,
      applicationFormFields: {
        ...form.applicationFormFields,
        [fieldName]: value,
      },
    });
  };

  const activator = () => {
    const jobsTemplates = pathOr([], ['jobs', 'jobs'])(templatesData);
    const template = jobsTemplates.find(({ id }) => id === selectedTemplateId);
    const selectedTitle = !!template ? template.title : 'Select ...';
    return (
      <div style={{ width: '100%' }}>
        <div className='Polaris-Labelled__LabelWrapper'>
          <div className='Polaris-Label'>
            <label id='PolarisSelect2Label' htmlFor='PolarisSelect2' className='Polaris-Label__Text'>
              Job template
            </label>
          </div>
        </div>
        <div className='Polaris-Select' onClick={togglePopoverActive} style={{ cursor: 'pointer' }}>
          <div className='Polaris-Select__Content' aria-hidden='true'>
            <span className='Polaris-Select__SelectedOption'>{selectedTitle}</span>
            <span className='Polaris-Select__Icon'>
              <span className='Polaris-Icon'>
                <svg viewBox='0 0 20 20' className='Polaris-Icon__Svg' focusable='false' aria-hidden='true'>
                  <path d='M13 8l-3-3-3 3h6zm-.1 4L10 14.9 7.1 12h5.8z' fillRule='evenodd'></path>
                </svg>
              </span>
            </span>
          </div>
          <div className='Polaris-Select__Backdrop'></div>
        </div>
      </div>
    );
  };

  const setOptionLabel = (index) => {
    switch (index) {
      case 0:
        return 'Option A';
      case 1:
        return 'Option B';
      case 2:
        return 'Option C';
      case 3:
        return 'Option D';
      case 4:
        return 'Option E';
      case 5:
        return 'Option F';
      case 6:
        return 'Option G';
      case 7:
        return 'Option H';
      default:
        return 'Option';
    }
  };

  const setQuestionTypeText = (type = 'single-line') => {
    switch (type) {
      case 'paragraph':
        return 'Paragraph text';
      case 'single-line':
        return 'Single line text';
      case 'boolean':
        return 'Boolean (Yes/No)';
      case 'multiple-choice':
        return 'Multiple choice';
      case 'checkboxes':
        return 'Checkboxes';
      case 'date-picker':
        return 'Date';
      case 'terms-and-conditions':
        return 'Terms & conditions';
      default:
        return 'Single line text';
    }
  };

  const checkForProhibitQuestion = () => {
    const invalidValues = [
      'Social Security Number',
      'Social Security #',
      'SSN',
      "Driver's License Number",
      'Drivers License Number',
      "Driver's License #",
      'DLN',
    ];
    let isValid = false;
    if (!form.customQuestion) return false;
    invalidValues.map((value) => {
      if (value.toLocaleLowerCase() === form.customQuestion.toLocaleLowerCase()) {
        isValid = true;
      }
    });
    return isValid;
  };

  return (
    <Mutation mutation={CREATE_JOB}>
      {(createJob) => (
        <Frame>
          <Page
            breadcrumbs={[ { content: 'Set job', onAction: () => { history.push('/set-job'); } },
              { content: 'Jobs', onAction: () => { history.push('/job-list'); } },
            ]}
            title={form.title && props.match.params.id ? form.title : 'Job setup'}
            secondaryActions={
              props.match.params.id
                ? [
                    {
                      content: 'Duplicate',
                      disabled: openJobs >= subscriptionPlan.number_jobs,
                      onAction: toggleDuplicateModal,
                    },
                    {
                      content: 'Preview',
                      disabled: job.status !== 'closed',
                      onAction: () => window.open(`https://${shopifyDomain}/a/careers/jobs/${props.match.params.id}/preview`),
                    },
                  ]
                : undefined
            }
            primaryAction={
              props.match.params.id ? (
                <ButtonGroup>
                  <Button
                    connectedDisclosure={{
                      accessibilityLabel: 'Job status',
                      disabled: job.archived || (openJobs >= subscriptionPlan.number_jobs && form.status == 'closed'),
                      actions: job.archived
                        ? [{ content: 'Unarchive', onAction: () => setArchiveModalActive(true) }]
                        : [
                            { content: 'Open', onAction: () => updateField('status')('open'), active: form.status === 'open' },
                            { content: 'Closed', onAction: () => updateField('status')('closed'), active: form.status !== 'open' },
                            { content: 'Archive', onAction: () => setArchiveModalActive(true) },
                          ],
                    }}>
                    {job.archived ? 'Archived' : form.status === 'open' ? 'Open' : 'Closed'}
                  </Button>
                  <Button primary onClick={() => updateJobForm()} disabled={job.archived || !validateForm()}>
                    Update
                  </Button>
                </ButtonGroup>
              ) : (
                {
                  content: 'Save',
                  onAction: () => onSubmit({ createJob }),
                }
              )
            }
            subtitle={
              !!props.match.params.id && form.status === 'open' ? (
                <Button
                  external
                  plain
                  monochrome
                  outline={false}
                  icon={ViewMajorMonotone}
                  url={`https://${props.prymaryUrl}/a/careers/jobs/${props.match.params.id}`}>
                  View
                </Button>
              ) : null
            }>
            {toastStatus.status && (
              <Toast content={toastStatus.message} onDismiss={() => setToastStatus({ status: false, message: '' })} />
            )}
            {showSuccessBanner && (
              <SuccessBanner
                title={`${form.title} ${titleUpdated ? 'updated' : 'posted'}`}
                history={history}
                onDismiss={() => setShowSuccessBanner(false)}
                portalBody={job.status !== 'closed' ? 'View on your career portal' : 'Preview job description page'}
                detailsBody={'view job details'}
                portalUrl={`https://${props.prymaryUrl}/a/careers/jobs/${props.match.params.id}${
                  job.status === 'closed' ? '/preview' : ''
                }`}
                detailsUrl={`/job-candidates/${props.match.params.id}`}
              />
            )}
            <Form onSubmit={() => onSubmit({ createJob })}>
              <hr className='hr' />
              <br />
              <Layout>
                <Layout.Section title='Job details'>
                  <Card title={'Details'} sectioned>
                    <FormLayout>
                      <FormLayout>
                        <Popover
                          fullWidth
                          preferredAlignment={'left'}
                          active={popoverActive}
                          activator={activator()}
                          onClose={togglePopoverActive}
                          title={'Job template'}>
                          <OptionList
                            id='template'
                            name='template'
                            label='Job template'
                            onChange={([selection]) => onChangeTemplateSelect(selection)}
                            sections={templateOptions}
                            selected={selectedTemplateId}
                          />
                        </Popover>
                        <span />
                      </FormLayout>
                      <FormLayout>
                        <p
                          className='Polaris-Layout__AnnotationDescription'
                          style={{
                            paddingLeft: '12px',
                            marginBottom: '10px',
                            paddingTop: '5px',
                          }}>
                          Speed up the job posting process by using a job template to populate the job setup fields.
                        </p>
                      </FormLayout>
                      <div>
                        <TextField id='title' label='Title' value={form.title} name='title' onChange={updateField('title')} />
                        {formErrors.title && <InlineError message='Title is required' fieldID='title' />}
                      </div>
                      <FormLayout.Group>
                        <div>
                          <Select
                            id='department'
                            label='Department'
                            options={[
                              ...departments.map(({ id: value, headingText: label }) => ({ value, label })),
                              { value: 'add_custom', label: '+ Add custom department' }
                            ]}
                            onChange={(value) => {
                              if (value === 'add_custom') {
                                toggleIsCreateDepartmentModalOpen();
                              } else {
                                updateField('department')(value);
                              }
                            }}
                            value={form.department}
                            placeholder={'Select ...'}
                          />
                          {formErrors.department && <InlineError message='Department is required' fieldID='department' />}
                        </div>
                        <div>
                          <Select
                            id='category'
                            label='Category'
                            options={categories}
                            onChange={updateField('category')}
                            value={form.category}
                            placeholder={'Select ...'}
                          />
                          {formErrors.category && <InlineError message='Category is required' fieldID='category' />}
                        </div>
                        <div>
                          <Select
                            id='employmentType'
                            label='Employment type'
                            options={employments}
                            onChange={updateField('employmentType')}
                            value={form.employmentType}
                          />
                          {formErrors.employmentType && (
                            <InlineError message='Employment type is required' fieldID='employmentType' />
                          )}
                        </div>
                      </FormLayout.Group>
                    </FormLayout>
                  </Card>
                </Layout.Section>
              </Layout>
              <br />
              <br />
              <Layout>
                <Layout.Section title='Settings'>
                  <Card title={'Settings'} sectioned>
                    <div>
                      <Checkbox
                        name='sendDisqualificationEmails'
                        id='sendDisqualificationEmails'
                        label='Send disqualification emails'
                        checked={form.sendDisqualificationEmails}
                        onChange={updateField('sendDisqualificationEmails')}
                      />
                    </div>
                  </Card>
                </Layout.Section>
              </Layout>
              <br />
              <br />
              <Layout>
                <Layout.Section title='Compensation'>
                  <Card title={'Compensation'} sectioned>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                      <p>
                        Display compensation on job postings is <b>{form.compensationEnabled ? 'Enable' : 'Disable'}</b>
                      </p>
                      <Button
                        primary={!form.compensationEnabled}
                        onClick={() => updateField('compensationEnabled')(!form.compensationEnabled)}>
                        {form.compensationEnabled ? 'Disable' : 'Enable'}
                      </Button>
                    </div>
                    {form.compensationEnabled && (
                      <FormLayout>
                        <br />
                        <FormLayout.Group>
                          <Select
                            id='currency'
                            label='Currency'
                            options={currencies}
                            onChange={updateField('currency')}
                            value={form.currency}
                          />
                          <span />
                        </FormLayout.Group>
                        <FormLayout.Group>
                          <Select
                            id='compensationType'
                            label='Compensation Type'
                            options={compensationTypes}
                            onChange={updateField('compensationType')}
                            value={form.compensationType}
                          />
                          <span />
                        </FormLayout.Group>
                        <FormLayout.Group>
                          <TextField
                            id='minCompensationRange'
                            value={form.minCompensationRange}
                            label='Minimum'
                            name='minCompensationRange'
                            type='number'
                            onChange={updateField('minCompensationRange')}
                          />
                          <TextField
                            id='maxCompensationRange'
                            value={form.maxCompensationRange}
                            name='maxCompensationRange'
                            label='Maximum'
                            type='number'
                            min={min}
                            max={max}
                            onChange={updateField('maxCompensationRange')}
                          />
                        </FormLayout.Group>

                        <Checkbox
                          name='plusComission'
                          id='plusComission'
                          label='Plus comission'
                          checked={form.plusComission}
                          onChange={updateField('plusComission')}
                        />
                        <FormLayout>
                          <p className='Polaris-Layout__AnnotationDescription' style={{ paddingLeft: '25px' }}>
                            Employees in this role will be able to receive a commission in addition to their base compensation.
                          </p>
                        </FormLayout>
                      </FormLayout>
                    )}
                  </Card>
                </Layout.Section>
              </Layout>
              <br />
              <br />
              <Layout>
                <Layout.Section title='Job description'>
                  <Card title='Description' sectioned>
                    <FormLayout>
                      {showEditor && (
                        <>
                          <Editor
                            initialValue={form.description || ''}
                            value={form.description}
                            init={{
                              height: 510,
                              menubar: false,
                              plugins: ['advlist autolink lists link', 'code fullscreen', 'wordcount', 'paste'],
                              toolbar:
                                'undo redo | formatselect | bold italic | \
                            alignleft aligncenter alignright alignjustify | \
                            bullist numlist outdent indent | removeformat | help',
                              paste_as_text: true,
                              paste_block_drop: false,
                            }}
                            id='description'
                            name='description'
                            apiKey='q23kp6s6a4hyvw6iuk3ub68yo4kqa2vj355xjdf1oi5w3x0b'
                            onEditorChange={handleEditorChange}
                          />

                          {formErrors.description && <InlineError message='Description is required' fieldID='description' />}
                        </>
                      )}
                    </FormLayout>
                  </Card>
                </Layout.Section>
              </Layout>
              <br />
              <br />
              <Layout>
                <Layout.Section title='Job location' description='This primary address where hired candidates will be working.'>
                  <Card title={'Select a job location'} sectioned>
                    <div className='location-search-container'>
                      <Icon source={SearchMajorMonotone} />
                      <TextField
                        placeholder='Search locations'
                        value={searchValue}
                        onChange={(value) => {
                          setSearchValue(value);
                          if (value !== '') {
                            const updatedLocations = companyLocations
                              .filter(({ name, city }) => {
                                const lowerValue = value?.toLocaleLowerCase();
                                const lowerName = name?.toLocaleLowerCase();
                                const lowerCity = city?.toLocaleLowerCase();

                                return lowerName?.includes(lowerValue) || lowerCity?.includes(lowerValue);
                              })
                              ?.slice();
                            setCompanyLocations(updatedLocations);
                            setLocationsToShow(updatedLocations.slice(0, 5));
                            setLocationsPage(1);
                          } else {
                            const initLocations = [...jobLocations.filter(({ name }) => name !== 'Custom'), ...fixedItems];
                            setCompanyLocations(initLocations);
                            setLocationsToShow(initLocations.slice(0, 5));
                            setLocationsPage(1);
                          }
                        }}
                      />
                    </div>
                    <ResourceList
                      resourceName={{ singular: 'location', plural: 'locations' }}
                      items={locationsToShow}
                      renderItem={onRenderLocationItems}
                    />
                    <div className='location-footer'>
                      <div className='location-footer-btn'>
                        <Button onClick={() => handleEditLocationModal()}>Add location</Button>
                      </div>
                      {companyLocations.length > 5 && (
                        <Pagination
                          hasPrevious={locationsPage > 1}
                          onPrevious={() => {
                            const prevPage = locationsPage - 1;
                            const upperLimit = 5 * prevPage;
                            const prevLocations = companyLocations.slice(upperLimit - 5, upperLimit);
                            setLocationsToShow(prevLocations);
                            setLocationsPage(prevPage);
                          }}
                          hasNext={locationsPage < Math.ceil(companyLocations.length / 5)}
                          onNext={() => {
                            const nextPage = locationsPage + 1;
                            const upperLimit = 5 * nextPage;
                            const nextLocations = companyLocations.slice(upperLimit - 5, upperLimit);
                            setLocationsToShow(nextLocations);
                            setLocationsPage(nextPage);
                          }}
                        />
                      )}
                    </div>
                    {formErrors.jobLocationName && <InlineError message='Job location is required' fieldID='category' />}
                  </Card>
                </Layout.Section>
                <Modal
                  open={isRemoveLocationModalOpen}
                  onClose={toggleIsRemoveLocationModalOpen}
                  title='Remove Location'
                  primaryAction={{
                    content: 'Remove',
                    onAction: async () => {
                      setLoading(true);
                      await deleteShopLocation({
                        variables: {
                          locationId: locationState?.locationId,
                          shopifyToken: dataProps.shop.shopify_token,
                        },
                      });
                      setTimeout(() => {
                        refetch();
                        toggleIsRemoveLocationModalOpen();
                        setLoading(false);
                      }, 550);
                    },
                  }}
                  secondaryActions={[
                    {
                      content: 'Cancel',
                      onAction: () => toggleIsRemoveLocationModalOpen(),
                    },
                  ]}
                  loading={false}>
                  <Modal.Section>
                    {isLoading ? (
                      <Spinner accessibilityLabel='Loading form field' size='large' />
                    ) : (
                      <TextStyle variation='strong'>
                        Are you sure you want to delete {locationState?.locationName}, {locationState?.address1}?
                      </TextStyle>
                    )}
                  </Modal.Section>
                </Modal>
                <Modal
                  open={locationModal}
                  onClose={handleLocationModal}
                  title='Edit location'
                  primaryAction={{
                    content: 'Update',
                    onAction: () => (stateLocationFields() ? validateLocationFields() : updateLocationFromModal()),
                  }}
                  secondaryActions={[
                    {
                      content: 'Cancel',
                      onAction: () => handleLocationModal(),
                    },
                  ]}
                  loading={false}>
                  <Modal.Section>{modalLocationMarkup}</Modal.Section>
                </Modal>
                <Modal
                  open={createLocationModal}
                  onClose={handleCreateLocationModal}
                  title='Add location'
                  primaryAction={{
                    content: 'Save',
                    disabled: stateLocationFields(),
                    onAction: () => (stateLocationFields() ? validateLocationFields() : addLocationFromModal()),
                  }}
                  secondaryActions={[
                    {
                      content: 'Cancel',
                      onAction: () => handleCreateLocationModal(),
                    },
                  ]}
                  loading={false}>
                  <Modal.Section>{modalLocationMarkup}</Modal.Section>
                </Modal>
                <Modal
                  open={isCustomQuestionModalOpen}
                  onClose={toggleIsCustomQuestionModalOpen}
                  title={selectedQuestionId ? 'Update custom question' : 'Add a custom question'}
                  primaryAction={{
                    content: `${selectedQuestionId ? 'Update' : 'Add'} question`,
                    onAction: () => {
                      if (checkForProhibitQuestion()) {
                        setInvalidQuestion(true);
                        return;
                      }
                      if (selectedQuestionId) {
                        const updatedQuestions = customQuestions.map((question) => {
                          if (question.id === selectedQuestionId) {
                            return {
                              ...question,
                              ...(form.customQuestion && {
                                question: form.customQuestion,
                              }),
                              type: form.customQuestionType,
                              ...(form.customQuestionType === 'terms-and-conditions' && {
                                termsAndConditions: {
                                  disclaimerText: form.termsAndConditions.disclaimerText,
                                  checkboxLabelText: form.termsAndConditions.checkboxLabelText,
                                },
                              }),
                              ...((form.customQuestionType === 'multiple-choice' || form.customQuestionType === 'checkboxes') && {
                                options: form.customQuestionOptions,
                              }),
                            };
                          }
                          return question;
                        });
                        setCustomQuestions(updatedQuestions);
                      } else {
                        setCustomQuestions([
                          ...customQuestions,
                          {
                            ...(form.customQuestion && {
                              question: form.customQuestion,
                            }),
                            ...(form.customQuestionType === 'terms-and-conditions' && {
                              termsAndConditions: {
                                disclaimerText: form.termsAndConditions.disclaimerText,
                                checkboxLabelText: form.termsAndConditions.checkboxLabelText,
                              },
                            }),
                            type: form.customQuestionType,
                            id: `${form.customQuestion[0] || form.termsAndConditions.disclaimerText[0]}-${lastQuestionId}`,
                            status: 'optional',
                            ...((form.customQuestionType === 'multiple-choice' || form.customQuestionType === 'checkboxes') && {
                              options: form.customQuestionOptions,
                            }),
                          },
                        ]);
                        setLastQuestionId(lastQuestionId + 1);
                      }
                      setSelectedQuestionId(null);
                      setState({
                        ...form,
                        customQuestion: '',
                        customQuestionType: '',
                        termsAndConditions: {
                          disclaimerText: '',
                          checkboxLabelText: '',
                        },
                        customQuestionOptions: [
                          { value: '', id: shortid.generate() },
                          { value: '', id: shortid.generate() },
                        ],
                      });
                      toggleIsCustomQuestionModalOpen();
                    },
                    disabled:
                      !form.customQuestion &&
                      (!form.termsAndConditions.disclaimerText || !form.termsAndConditions.checkboxLabelText),
                  }}
                  secondaryActions={[
                    {
                      content: 'Cancel',
                      onAction: () => {
                        setState({
                          ...form,
                          customQuestion: '',
                          customQuestionType: '',
                          termsAndConditions: {
                            disclaimerText: '',
                            checkboxLabelText: '',
                          },
                          customQuestionOptions: [
                            { value: '', id: shortid.generate() },
                            { value: '', id: shortid.generate() },
                          ],
                        });
                        setSelectedQuestionId(null);
                        toggleIsCustomQuestionModalOpen();
                      },
                    },
                  ]}
                  loading={false}>
                  <Modal.Section>
                    <FormLayout>
                      <Select
                        label='Question type'
                        options={[
                          { label: 'Single line text', value: 'single-line' },
                          { label: 'Paragraph text', value: 'paragraph' },
                          { label: 'Boolean (Yes/No)', value: 'boolean' },
                          { label: 'Multiple choice', value: 'multiple-choice' },
                          { label: 'Checkboxes', value: 'checkboxes' },
                          { label: 'Date Picker (DD-MM-YYYY)', value: 'date-picker' },
                          { label: 'Terms & conditions', value: 'terms-and-conditions' },
                        ]}
                        onChange={updateField('customQuestionType')}
                        value={form.customQuestionType}
                      />
                      {invalidQuestion && (
                        <p style={{ color: 'Red' }}>
                          The use of ShopHire to collect personal information(SSN, DL Number, TaxID) is strictly prohibited per
                          <Link url={`https://www.shophire.co/terms-and-conditions`} removeUnderline external>
                            {' '}
                            our terms of service
                          </Link>
                          .
                        </p>
                      )}
                      {form.customQuestionType !== 'terms-and-conditions' && (
                        <TextField
                          id='customQuestion'
                          label='Question'
                          value={form.customQuestion}
                          name='customQuestion'
                          onChange={updateField('customQuestion')}
                          multiline={4}
                        />
                      )}
                      {form.customQuestionType === 'terms-and-conditions' && (
                        <div>
                          <TextField
                            id='disclaimerText'
                            label='Disclaimer text'
                            value={form.termsAndConditions.disclaimerText}
                            name='disclaimerText'
                            onChange={updateField('disclaimerText')}
                            multiline={4}
                          />
                          <br />
                          <TextField
                            id='checkboxLabelText'
                            label='Checkbox label text'
                            value={form.termsAndConditions.checkboxLabelText}
                            name='checkboxLabelText'
                            onChange={updateField('checkboxLabelText')}
                            multiline={1}
                          />
                        </div>
                      )}
                      {(form.customQuestionType === 'multiple-choice' || form.customQuestionType === 'checkboxes') && (
                        <div style={{ padding: '0 20px' }}>
                          {form.customQuestionOptions.map(({ id, value }, index) => (
                            <div
                              style={{
                                marginBottom: '10px',
                                display: 'grid',
                                alignItems: 'center',
                                gridTemplateColumns: '95% 1fr',
                              }}
                              key={id}>
                              <TextField
                                id={id}
                                label={setOptionLabel(index)}
                                value={value}
                                onChange={updateField('customQuestionOptions', id)}
                              />
                              <span
                                style={{ width: '30px', display: 'inline-block', margin: '20px 0 0 10px', cursor: 'pointer' }}
                                onClick={() => {
                                  setState({
                                    ...form,
                                    customQuestionOptions: form.customQuestionOptions.filter((option) => option.id !== id),
                                  });
                                }}>
                                <Icon source={DeleteMajorMonotone} />
                              </span>
                            </div>
                          ))}
                          {form.customQuestionOptions.length < (form.customQuestionType === 'checkboxes' ? 8 : 5) && (
                            <div
                              onClick={() => {
                                setState({
                                  ...form,
                                  customQuestionOptions: [...form.customQuestionOptions, { value: '', id: shortid.generate() }],
                                });
                              }}
                              style={{ display: 'inline-flex', cursor: 'pointer', marginTop: '15px' }}>
                              <div style={{ marginRight: '1rem' }}>
                                <Icon source={CirclePlusMinor} />
                              </div>{' '}
                              <TextStyle variation='subdued'>Add another option</TextStyle>
                            </div>
                          )}
                        </div>
                      )}
                    </FormLayout>
                  </Modal.Section>
                </Modal>
                <Modal
                  open={isCreateDepartmentModalOpen}
                  onClose={toggleIsCreateDepartmentModalOpen}
                  title='Create Custom Department'
                  primaryAction={{
                    content: 'Create Department',
                    onAction: handleCreateDepartment,
                    disabled: !newDepartmentName.trim() || loading,
                  }}
                  secondaryActions={[
                    {
                      content: 'Cancel',
                      onAction: toggleIsCreateDepartmentModalOpen,
                    },
                  ]}
                  loading={loading}>
                  <Modal.Section>
                    <FormLayout>
                      <TextField
                        label='Department Name'
                        value={newDepartmentName}
                        onChange={handleDepartmentNameChange}
                        onBlur={handleDepartmentNameBlur}
                        placeholder='Enter department name'
                        error={departmentError}
                        autoComplete='off'
                      />
                    </FormLayout>
                  </Modal.Section>
                </Modal>
              </Layout>
              <br />
              <br />
              <Layout>
                <Layout.Section
                  title={'Hiring team'}
                  description='Add team members to the open, so they can receive email notifications'>
                  <Card title={'Hiring team'} sectioned>
                    {isEmpty(hiringTeam) && (
                      <>
                        <EmptyState
                          heading='No team members found'
                          action={{ content: 'Add team member', onAction: () => handleEditMemberModal() }}>
                          <p>Add team members to the open, so they can receive email notifications</p>
                        </EmptyState>
                        <br />
                      </>
                    )}
                    {!isEmpty(hiringTeam) && (
                      <div className={'TeamMembers__list'} style={{ maxHeight: '30vh', overflow: 'scroll' }}>
                        <ResourceList
                          resourceName={{ singular: 'team member', plural: 'team members' }}
                          items={hiringTeam}
                          renderItem={(item) => {
                            const { firstName, lastName, email, id } = item;
                            return (
                              <ResourceItem
                                id={id}
                                shortcutActions={[
                                  {
                                    content: 'Edit',
                                    onAction: () => handleEditMemberModal(item),
                                  },
                                  {
                                    content: 'Remove',
                                    onAction: () => handleRemoveMemberModal(item),
                                  },
                                ]}>
                                <Stack>
                                  <Checkbox
                                    checked={selectedMembers.indexOf(id) !== -1}
                                    onChange={(value) => {
                                      if (value) {
                                        setSelectedMembers([...selectedMembers, id]);
                                        setFormErrors({ ...formErrors, teamMemberIds: false });
                                      } else {
                                        setSelectedMembers(selectedMembers.filter((item) => item !== id));
                                      }
                                    }}
                                  />
                                  <Stack.Item fill>
                                    <span
                                      onClick={() => {
                                        const value = !!selectedMembers.find((item) => item === id);
                                        if (!value) {
                                          setSelectedMembers([...selectedMembers, id]);
                                          setFormErrors({ ...formErrors, teamMemberIds: false });
                                        } else {
                                          setSelectedMembers(selectedMembers.filter((item) => item !== id));
                                        }
                                      }}>
                                      <TextStyle variation='strong'>
                                        {firstName} {lastName}
                                      </TextStyle>
                                      <div>{email}</div>
                                    </span>
                                  </Stack.Item>
                                </Stack>
                              </ResourceItem>
                            );
                          }}
                        />
                      </div>
                    )}
                    <div className='team-member-footer'>
                      <div className='location-footer-btn' style={{ marginBottom: 5 }}>
                        <Button onClick={() => handleEditMemberModal()}>Add team member</Button>
                      </div>
                      {formErrors.teamMemberIds && <InlineError message='Hiring team member is required' fieldID='category' />}
                    </div>
                  </Card>
                </Layout.Section>
                <Modal
                  open={isRemoveMemberModalOpen}
                  onClose={toggleIsRemoveMemberModalOpen}
                  title='Remove Team Member'
                  primaryAction={{
                    content: 'Remove',
                    onAction: async () => {
                      setLoading(true);
                      await deleteMember({
                        variables: {
                          memberId: memberState?.memberId,
                          shopifyToken: dataProps.shop.shopify_token,
                        },
                      });
                      setTimeout(() => {
                        refetchTeam();
                        toggleIsRemoveMemberModalOpen();
                        setLoading(false);
                      }, 550);
                    },
                  }}
                  secondaryActions={[
                    {
                      content: 'Cancel',
                      onAction: () => toggleIsRemoveMemberModalOpen(),
                    },
                  ]}
                  loading={loading}>
                  <Modal.Section>
                    <TextStyle variation='strong'>
                      Are you sure you want to remove{' '}
                      <strong>
                        {memberState?.memberFirstName} {memberState?.memberLastName}
                      </strong>{' '}
                      from team?
                    </TextStyle>
                  </Modal.Section>
                </Modal>
                <Modal
                  open={memberModal}
                  onClose={handleMemberModal}
                  title='Edit Team Meber'
                  primaryAction={{
                    content: 'Update',
                    onAction: () => (stateMemberFields() ? validateMemberFields() : updateMemberFromModal()),
                  }}
                  secondaryActions={[
                    {
                      content: 'Cancel',
                      onAction: () => handleMemberModal(),
                    },
                  ]}
                  loading={loading}>
                  <Modal.Section>{modalMemberMarkup}</Modal.Section>
                </Modal>
                <Modal
                  open={createMemberModal}
                  onClose={handleCreateMemberModal}
                  title='Add team member'
                  primaryAction={{
                    content: 'Save',
                    disabled: stateMemberFields(),
                    onAction: () => (stateMemberFields() ? validateMemberFields() : addMemberFromModal()),
                  }}
                  secondaryActions={[
                    {
                      content: 'Cancel',
                      onAction: () => handleCreateMemberModal(),
                    },
                  ]}
                  loading={loading}>
                  <Modal.Section>{modalMemberMarkup}</Modal.Section>
                </Modal>
              </Layout>
              <br />
              <br />

              <Layout>
                <Layout.Section
                  title='Application form'
                  description='Select which questions you want to include on the job application.'>
                  <Card title='Application form' sectioned>
                    {!showApplicationFormButtons ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div>
                            <Icon source={LockMinor} />
                          </div>
                          <h2 className='Polaris-TextStyle--variationStrong'>Customize your application form</h2>
                        </div>
                        <div style={{ margin: '10px 0 0 30px' }}>
                          <Link onClick={() => history.push('/account-plan')} removeUnderline>
                            Upgrade account plan
                          </Link>
                        </div>
                      </>
                    ) : (
                      <>
                        <Card.Section>
                          <h2 className='Polaris-TextStyle--variationStrong'>Name</h2>
                        </Card.Section>
                        <Card.Section>
                          <h2 className='Polaris-TextStyle--variationStrong'>Email</h2>
                        </Card.Section>
                        <Card.Section>
                          <div className='job-application-row'>
                            <h2 className='Polaris-TextStyle--variationStrong'>Phone</h2>
                            <ButtonGroup segmented>
                              <Button
                                primary={form?.applicationFormFields?.phone === 'required'}
                                onClick={toggleAppFormFields({ fieldName: 'phone', value: 'required' })}>
                                Required
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.phone === 'optional'}
                                onClick={toggleAppFormFields({ fieldName: 'phone', value: 'optional' })}>
                                Optional
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.phone === 'disabled'}
                                onClick={toggleAppFormFields({ fieldName: 'phone', value: 'disabled' })}>
                                Disabled
                              </Button>
                            </ButtonGroup>
                          </div>
                        </Card.Section>
                        <Card.Section>
                          <div className='job-application-row'>
                            <h2 className='Polaris-TextStyle--variationStrong'>Address</h2>
                            <ButtonGroup segmented>
                              <Button
                                primary={form?.applicationFormFields?.address === 'required'}
                                onClick={toggleAppFormFields({ fieldName: 'address', value: 'required' })}>
                                Required
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.address === 'optional'}
                                onClick={toggleAppFormFields({ fieldName: 'address', value: 'optional' })}>
                                Optional
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.address === 'disabled'}
                                onClick={toggleAppFormFields({ fieldName: 'address', value: 'disabled' })}>
                                Disabled
                              </Button>
                            </ButtonGroup>
                          </div>
                        </Card.Section>
                        <Card.Section>
                          <div className='job-application-row'>
                            <h2 className='Polaris-TextStyle--variationStrong'>Work Experience</h2>
                            <ButtonGroup segmented>
                              <Button
                                primary={form?.applicationFormFields?.work_experience === 'required'}
                                onClick={toggleAppFormFields({ fieldName: 'work_experience', value: 'required' })}>
                                Required
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.work_experience === 'optional'}
                                onClick={toggleAppFormFields({ fieldName: 'work_experience', value: 'optional' })}>
                                Optional
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.work_experience === 'disabled'}
                                onClick={toggleAppFormFields({ fieldName: 'work_experience', value: 'disabled' })}>
                                Disabled
                              </Button>
                            </ButtonGroup>
                          </div>
                        </Card.Section>
                        <Card.Section>
                          <div className='job-application-row'>
                            <h2 className='Polaris-TextStyle--variationStrong'>Linkedin URL</h2>
                            <ButtonGroup segmented>
                              <Button
                                primary={form?.applicationFormFields?.linkedin_url === 'required'}
                                onClick={toggleAppFormFields({ fieldName: 'linkedin_url', value: 'required' })}>
                                Required
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.linkedin_url === 'optional'}
                                onClick={toggleAppFormFields({ fieldName: 'linkedin_url', value: 'optional' })}>
                                Optional
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.linkedin_url === 'disabled'}
                                onClick={toggleAppFormFields({ fieldName: 'linkedin_url', value: 'disabled' })}>
                                Disabled
                              </Button>
                            </ButtonGroup>
                          </div>
                        </Card.Section>
                        <Card.Section>
                          <div className='job-application-row'>
                            <h2 className='Polaris-TextStyle--variationStrong'>GitHub URL</h2>
                            <ButtonGroup segmented>
                              <Button
                                primary={form?.applicationFormFields?.github_url === 'required'}
                                onClick={toggleAppFormFields({ fieldName: 'github_url', value: 'required' })}>
                                Required
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.github_url === 'optional'}
                                onClick={toggleAppFormFields({ fieldName: 'github_url', value: 'optional' })}>
                                Optional
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.github_url === 'disabled'}
                                onClick={toggleAppFormFields({ fieldName: 'github_url', value: 'disabled' })}>
                                Disabled
                              </Button>
                            </ButtonGroup>
                          </div>
                        </Card.Section>
                        <Card.Section>
                          <div className='job-application-row'>
                            <h2 className='Polaris-TextStyle--variationStrong'>Portfolio URL</h2>
                            <ButtonGroup segmented>
                              <Button
                                primary={form?.applicationFormFields?.portfolio_url === 'required'}
                                onClick={toggleAppFormFields({ fieldName: 'portfolio_url', value: 'required' })}>
                                Required
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.portfolio_url === 'optional'}
                                onClick={toggleAppFormFields({ fieldName: 'portfolio_url', value: 'optional' })}>
                                Optional
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.portfolio_url === 'disabled'}
                                onClick={toggleAppFormFields({ fieldName: 'portfolio_url', value: 'disabled' })}>
                                Disabled
                              </Button>
                            </ButtonGroup>
                          </div>
                        </Card.Section>
                        <Card.Section>
                          <div className='job-application-row'>
                            <h2 className='Polaris-TextStyle--variationStrong'>Personal Website</h2>
                            <ButtonGroup segmented>
                              <Button
                                primary={form?.applicationFormFields?.personal_website === 'required'}
                                onClick={toggleAppFormFields({ fieldName: 'personal_website', value: 'required' })}>
                                Required
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.personal_website === 'optional'}
                                onClick={toggleAppFormFields({ fieldName: 'personal_website', value: 'optional' })}>
                                Optional
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.personal_website === 'disabled'}
                                onClick={toggleAppFormFields({ fieldName: 'personal_website', value: 'disabled' })}>
                                Disabled
                              </Button>
                            </ButtonGroup>
                          </div>
                        </Card.Section>
                        <Card.Section>
                          <div className='job-application-row'>
                            <h2 className='Polaris-TextStyle--variationStrong'>Resume</h2>
                            <ButtonGroup segmented>
                              <Button
                                primary={form?.applicationFormFields?.resume === 'required'}
                                onClick={toggleAppFormFields({ fieldName: 'resume', value: 'required' })}>
                                Required
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.resume === 'optional'}
                                onClick={toggleAppFormFields({ fieldName: 'resume', value: 'optional' })}>
                                Optional
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.resume === 'disabled'}
                                onClick={toggleAppFormFields({ fieldName: 'resume', value: 'disabled' })}>
                                Disabled
                              </Button>
                            </ButtonGroup>
                          </div>
                        </Card.Section>
                        <Card.Section>
                          <div className='job-application-row'>
                            <h2 className='Polaris-TextStyle--variationStrong'>Cover Letter</h2>
                            <ButtonGroup segmented>
                              <Button
                                primary={form?.applicationFormFields?.cover_letter === 'required'}
                                onClick={toggleAppFormFields({ fieldName: 'cover_letter', value: 'required' })}>
                                Required
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.cover_letter === 'optional'}
                                onClick={toggleAppFormFields({ fieldName: 'cover_letter', value: 'optional' })}>
                                Optional
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.cover_letter === 'disabled'}
                                onClick={toggleAppFormFields({ fieldName: 'cover_letter', value: 'disabled' })}>
                                Disabled
                              </Button>
                            </ButtonGroup>
                          </div>
                        </Card.Section>
                        <Card.Section>
                          <div className='job-application-row'>
                            <h2 className='Polaris-TextStyle--variationStrong'>Newsletter sign up</h2>
                            <ButtonGroup segmented>
                              <Button
                                primary={form?.applicationFormFields?.newsletter_sign_up === 'optional'}
                                onClick={toggleAppFormFields({ fieldName: 'newsletter_sign_up', value: 'optional' })}>
                                Optional
                              </Button>
                              <Button
                                primary={form?.applicationFormFields?.newsletter_sign_up === 'disabled'}
                                onClick={toggleAppFormFields({ fieldName: 'newsletter_sign_up', value: 'disabled' })}>
                                Disabled
                              </Button>
                            </ButtonGroup>
                          </div>
                        </Card.Section>
                        <Card.Section>
                          <br />
                          <h2 className='Polaris-TextStyle--variationStrong' style={{ marginBottom: '15px' }}>
                            Custom Questions
                          </h2>
                          <div className='application-form__list'>
                            <ResourceList
                              items={customQuestions}
                              renderItem={(item) => {
                                const { id, question, status, type, options, termsAndConditions } = item;
                                return (
                                  <div key={id} className='job-application-row' style={{ padding: '20px 0' }}>
                                    <div>
                                      <p style={{ maxWidth: '260px' }}>
                                        {question ? question : termsAndConditions.disclaimerText}
                                      </p>
                                      <TextStyle variation='subdued'>{setQuestionTypeText(type)}</TextStyle>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                      <span
                                        style={{ width: '30px', display: 'inline-block', cursor: 'pointer' }}
                                        onClick={() => {
                                          setSelectedQuestionId(id);
                                          setState({
                                            ...form,
                                            customQuestion: question,
                                            customQuestionType: type,
                                            ...((type === 'multiple-choice' || type === 'checkboxes') && {
                                              customQuestionOptions: options,
                                            }),
                                            ...(type === 'terms-and-conditions' && {
                                              termsAndConditions: {
                                                disclaimerText: termsAndConditions.disclaimerText,
                                                checkboxLabelText: termsAndConditions.checkboxLabelText,
                                              },
                                            }),
                                          });
                                          toggleIsCustomQuestionModalOpen();
                                        }}>
                                        <Icon source={EditMajorMonotone} />
                                      </span>
                                      <span
                                        style={{ width: '30px', display: 'inline-block', marginRight: '8px', cursor: 'pointer' }}
                                        onClick={() => {
                                          const updatedQuestions = customQuestions.filter(
                                            ({ id: questionId }) => questionId !== id
                                          );
                                          setCustomQuestions(updatedQuestions);
                                        }}>
                                        <Icon source={DeleteMajorMonotone} />
                                      </span>
                                      <ButtonGroup segmented>
                                        <Button
                                          primary={status === 'required'}
                                          onClick={() => {
                                            const updatedQuestion = customQuestions.map((prevQuestion) => {
                                              if (prevQuestion.id === id) {
                                                return {
                                                  ...prevQuestion,
                                                  status: 'required',
                                                };
                                              }
                                              return prevQuestion;
                                            });
                                            setCustomQuestions(updatedQuestion);
                                          }}>
                                          Required
                                        </Button>
                                        <Button
                                          primary={status === 'optional'}
                                          onClick={() => {
                                            const updatedQuestion = customQuestions.map((prevQuestion) => {
                                              if (prevQuestion.id === id) {
                                                return {
                                                  ...prevQuestion,
                                                  status: 'optional',
                                                };
                                              }
                                              return prevQuestion;
                                            });
                                            setCustomQuestions(updatedQuestion);
                                          }}>
                                          Optional
                                        </Button>
                                      </ButtonGroup>
                                    </div>
                                  </div>
                                );
                              }}
                            />
                          </div>
                          {customQuestions.length < 10 && (
                            <div className='left-hand-footer'>
                              <div></div>
                              <Button
                                onClick={toggleIsCustomQuestionModalOpen}
                                style={{ display: 'inline-flex', cursor: 'pointer', marginTop: '20px' }}>
                                Add a custom question
                              </Button>
                            </div>
                          )}
                        </Card.Section>
                      </>
                    )}
                  </Card>
                </Layout.Section>
              </Layout>
              <br />
              <br />
              {!isEmpty(questionnaires) && (
                <>
                  <Layout>
                    <Layout.Section
                      title={'Questionnaires'}
                      description='Extend your application form using questionnaires to collect references, video responses and more'>
                      <Card title={'Questionnaires'} sectioned>
                        {!showApplicationFormButtons ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div>
                                <Icon source={LockMinor} />
                              </div>
                              <h2 className='Polaris-TextStyle--variationStrong'>
                                Send an automated quiz/questionnaire after the submit a job application
                              </h2>
                            </div>
                            <div style={{ margin: '10px 0 0 30px' }}>
                              <Link onClick={() => history.push('/account-plan')} removeUnderline>
                                Upgrade account plan
                              </Link>
                            </div>
                          </>
                        ) : (
                          <div className={'TeamMembers__list'} style={{ maxHeight: '40vh', height: '40vh', overflow: 'scroll' }}>
                            <ResourceList
                              resourceName={{ singular: 'questionnaire', plural: 'questionnaires' }}
                              items={questionnaires}
                              renderItem={(item) => {
                                const { id, name, updatedAt, fields = [] } = item;
                                return (
                                  <ResourceItem id={id}>
                                    <Stack>
                                      <RadioButton
                                        checked={selectedQuestionnaires.indexOf(id) !== -1}
                                        onChange={(value) => {
                                          if (selectedQuestionnaires.indexOf(id) === -1) {
                                            setSelectedQuestionnaires([id]);
                                          } else {
                                            setSelectedQuestionnaires([]);
                                          }
                                        }}
                                      />
                                      <Stack.Item fill>
                                        <span
                                          onClick={() => {
                                            if (selectedQuestionnaires.indexOf(id) === -1) {
                                              setSelectedQuestionnaires([id]);
                                            } else {
                                              setSelectedQuestionnaires([]);
                                            }
                                          }}>
                                          <span>
                                            <TextStyle variation='strong'>{name}</TextStyle>
                                          </span>
                                          <div>Updated {moment(updatedAt).format('MMMM D, YYYY')}</div>
                                        </span>
                                      </Stack.Item>
                                      <p style={{ width: '15vw', display: 'flex', alignItems: 'center', height: '100%' }}>{`${
                                        (fields || []).length
                                      } Questions`}</p>
                                      <div
                                        style={{
                                          width: '100px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          height: '100%',
                                          justifyContent: 'center',
                                        }}
                                        onClick={() =>
                                          window.open(`https://${shopifyDomain}/a/careers/preview-questionnaire?q=${item.id}`)
                                        }>
                                        <Tooltip content='Preview questionnaire template' dismissOnMouseOut>
                                          <Icon source={ViewMajorMonotone} />
                                        </Tooltip>
                                      </div>
                                    </Stack>
                                  </ResourceItem>
                                );
                              }}
                            />
                          </div>
                        )}
                      </Card>
                    </Layout.Section>
                  </Layout>
                  <br />
                  {/* Job Translations Section */}
                  {props.match.params.id && (
                    <Layout>
                      <Layout.Section
                        title={'Job Translations'}
                        description='Translate your job postings into multiple languages to reach a global audience'>
                        <Card title={'Job Translations'} sectioned>
                          {!showApplicationFormButtons ? (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div>
                                  <Icon source={LockMinor} />
                                </div>
                                <h2 className='Polaris-TextStyle--variationStrong'>
                                  Translate your job postings into multiple languages
                                </h2>
                              </div>
                              <div style={{ margin: '10px 0 0 30px' }}>
                                <Link onClick={() => history.push('/account-plan')} removeUnderline>
                                  Upgrade account plan
                                </Link>
                              </div>
                            </>
                          ) : (
                            <JobTranslationManager
                              job={job}
                              shopifyToken={dataProps.shop.shopify_token}
                              onTranslationUpdate={() => {
                                // Refetch job data to get updated translations
                                if (jobRefetch) jobRefetch();
                              }}
                            />
                          )}
                        </Card>
                      </Layout.Section>
                    </Layout>
                  )}
                  
                  <br />
                </>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button onClick={() => history.push('/job-list')} size='large'>
                  Cancel
                </Button>
                {renderButton}
              </div>
            </Form>

            <Modal
              open={duplicateModal}
              onClose={toggleDuplicateModal}
              title={'Duplicate job'}
              primaryAction={{
                content: `Yes, duplicate job`,
                onAction: () => createJobFromModal({ createJob, status: 'closed', duplicate: true }),
              }}
              secondaryActions={[
                {
                  content: 'Cancel',
                  onAction: toggleDuplicateModal,
                },
              ]}
              loading={false}>
              <Modal.Section>
                <p>{`Do you want to duplicate this job?`}</p>
              </Modal.Section>
            </Modal>

            <Modal
              open={archiveModalActive}
              onClose={() => setArchiveModalActive(false)}
              title={job.archived ? 'Unarchive job' : 'Archive job'}
              primaryAction={{
                content: job.archived ? 'Yes, unarchive job' : 'Yes, archive job',
                onAction: job.archived ? onUnarchiveJob : onArchiveJob,
                disabled: isLoading,
              }}
              secondaryActions={[
                {
                  content: 'Cancel',
                  onAction: () => setArchiveModalActive(false),
                  disabled: isLoading,
                },
              ]}
              loading={isLoading}>
              <Modal.Section>
                <TextContainer>
                  <p>{`Are you sure you want to ${job.archived ? 'unarchive' : 'archive'} "${form.title}"?`}</p>
                  {!job.archived && (
                    <p style={{ color: '#637381' }}>
                      Note: Archiving a job will close it and hide it from your career portal. You can unarchive it later.
                    </p>
                  )}
                </TextContainer>
              </Modal.Section>
            </Modal>

            <Modal
              open={active}
              onClose={() => setActive(false)}
              primaryAction={{
                content: 'Publish open job',
                onAction: () => createJobFromModal({ createJob, status: 'open' }),
                disabled: isLoading,
              }}
              secondaryActions={[
                {
                  content: 'Save closed job',
                  onAction: () => createJobFromModal({ createJob, status: 'closed' }),
                  disabled: isLoading,
                },
              ]}
              loading={false}>
              <div class='Polaris-Modal-Header'>
                <div id='Polarismodal-header4' class='Polaris-Modal-Header__Title'>
                  <h2 class='Polaris-DisplayText Polaris-DisplayText--sizeSmall'>{`Do you want to open the ${form.title} job?`}</h2>
                </div>
                <button class='Polaris-Modal-CloseButton job-Modal-CloseButton' aria-label='Close' onClick={handleModal}>
                  <span class='Polaris-Icon Polaris-Icon--colorBase Polaris-Icon--applyColor'>
                    <svg viewBox='0 0 20 20' class='Polaris-Icon__Svg' focusable='false' aria-hidden='true'>
                      <path d='M11.414 10l6.293-6.293a1 1 0 1 0-1.414-1.414L10 8.586 3.707 2.293a1 1 0 0 0-1.414 1.414L8.586 10l-6.293 6.293a1 1 0 1 0 1.414 1.414L10 11.414l6.293 6.293A.998.998 0 0 0 18 17a.999.999 0 0 0-.293-.707L11.414 10z'></path>
                    </svg>
                  </span>
                </button>
              </div>
              <Modal.Section>{modalMarkup}</Modal.Section>
            </Modal>
          </Page>
        </Frame>
      )}
    </Mutation>
  );
};

export default JobSetup;
